import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { Globe, Ban, Share2, Sparkles, ArrowUp, Plus } from "lucide-react";
import { UNSAFE_LocationContext as LocationContext, UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import './ModalComponents.css';

import { authService } from "../../services/auth";
import { API_BASE, FRONTEND_URL } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { GROUP_ICON, RATING_ICON, TIMER_ICON, WARNING_ICON, GAME_ICON } from "../../constants/icons";
import AddGameModal from "./AddGameModal";
import PlayListButton from "../common/PlayListButton";
import logger from '../../utils/logger';
import { useMarketplaceSummary } from "../../hooks/useMarketplaceSummary";
import { isFirefox } from "../../utils/browserDetection";
import ShareWarningBanner from "../common/ShareWarningBanner";
import "../../styles/share-warning.css";

const useOptionalRouter = () => {
  const locationContext = useContext(LocationContext);
  const navigationContext = useContext(NavigationContext);

  const navigate = useCallback((to, options = {}) => {
    if (navigationContext?.navigator) {
      const nav = navigationContext.navigator;
      if (options?.state) {
        nav.push(to, options.state);
      } else {
        nav.push(to);
      }
    }
  }, [navigationContext]);

  const location = locationContext?.location || null;

  return { navigate, location };
};

const getVenueDisplayName = (venueCode) => {
  const venueNames = {
    'bga': 'BGA',
    'steam': 'Steam',
    'epic': 'Epic',
    'gog': 'GOG',
    'origin': 'Origin',
    'uplay': 'Uplay',
    'tabletopsimulator': 'Tabletop Sim',
    'yucata': 'Yucata',
    'online': 'Online',
  };
  return venueNames[venueCode] || venueCode.toUpperCase();
};

const formatOnlineInfo = (onlineVenues) => {
  if (!onlineVenues || onlineVenues.length === 0) return null;

  const venuesText = onlineVenues
    .map((venue) => getVenueDisplayName(venue.venue_code))
    .join(', ');
  const isGenericOnline = onlineVenues.length === 1 && onlineVenues[0].venue_code === 'online';
  const hasPremium = onlineVenues.some((venue) => venue.access_type === 'premium_required');

  return {
    text: isGenericOnline
      ? (<> <Globe size={14} className="pista-green-icon" /> Online</>)
      : (<> <Globe size={14} className="pista-green-icon" /> {venuesText}</>),
    title: isGenericOnline
      ? 'Available to play online'
      : `Available online: ${venuesText}${hasPremium ? ' (Premium required for some venues)' : ''}`,
  };
};

const formatDesigners = (designers, maxVisible = 2) => {
  if (!designers || designers.length === 0) return null;

  const visibleDesigners = designers.slice(0, maxVisible);
  const hasMore = designers.length > maxVisible;

  return (
    <span className="curate-game-tile-subtitle-designers">
      {visibleDesigners.map((designer, idx) => (
        <span key={idx}>
          <span className="curate-game-tile-subtitle-designer">
            {designer}
          </span>
          {idx < visibleDesigners.length - 1 && ", "}
        </span>
      ))}
      {hasMore && (
        <span className="curate-game-tile-subtitle-designers-more">
          ... {designers.length - maxVisible} more
        </span>
      )}
    </span>
  );
};

function GameTile({
  game: r,
  chipsToShow,
  variant,
  differences,
  querySpec,
  onAddToSearch,
  setPreviewGame,
  showAddToCollection,
  isInCollection,
  onAddToCollection,
  onLetSPlay,
  gameChips,
  _hideMarketplaceButton,
  hideSimilarity,
  hideFeedback,
  onCompare,
  user,
  getGameOwnershipStatus,
  onlineVenues: onlineVenuesProp,
  getGameAvailability,
  onSearchSimilar,
  onAddToMarketplace,
  _onAddReminder,
  _onCompareGames,
  onTogglePlayList,
  inPlayList,
  onExcludeGame,
  hideOpenInExplore,
}) {
  const [userRating, setUserRating] = useState(null);
  const [shareStatus, setShareStatus] = useState("");
  const [showShareWarning, setShowShareWarning] = useState(false);
  const shareTimeoutRef = useRef(null);
  const [addGameModal, setAddGameModal] = useState(null);
  const [feedbackSignal, setFeedbackSignal] = useState(null);
  const [excludeStatus, setExcludeStatus] = useState(null);

  const marketplaceGameId = r?.game_id || r?.id;
  const { summary: marketplaceSummary } = useMarketplaceSummary(
    marketplaceGameId,
    Boolean(onAddToMarketplace)
  );

  // Reset exclude state when the game changes so stale status doesn't bleed into a newly selected game
  useEffect(() => {
    setExcludeStatus(null);
  }, [marketplaceGameId]);

  // Fetch user rating effect
  useEffect(() => {
    const fetchUserRating = async () => {
      if (user?.id && marketplaceGameId) {
        try {
          const response = await httpRequest(
            `${API_BASE}/profile/collection?game_id=${marketplaceGameId}&user_id=${user.id}`,
            {
              method: "GET",
              headers: authService.getAuthHeaders(),
            }
          );

          if (response.ok) {
            const data = await response.json();
            setUserRating(data.preference || null);
          }
        } catch (error) {
          logger.warn("Failed to fetch user rating:", error);
        }
      }
    };

    fetchUserRating();
  }, [user?.id, marketplaceGameId]);

  const handleFeedback = async (signal) => {
    if (!user?.id || !r?.game_id) return;
    const next = feedbackSignal === signal ? null : signal;
    setFeedbackSignal(next);
    if (!next) return;
    try {
      await httpRequest(`${API_BASE}/recommendations/feedback`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_id: r.game_id,
          base_game_id: r.base_game_id ?? null,
          signal: next,
        }),
      });
    } catch (err) {
      logger.warn("Failed to record recommendation feedback:", err);
    }
  };

  // Share timeout cleanup effect
  useEffect(() => {
    const shareTimeoutRefCurrent = shareTimeoutRef;
    return () => {
      if (shareTimeoutRefCurrent.current) {
        clearTimeout(shareTimeoutRefCurrent.current);
      }
    };
  }, []);

  const hasGamesInContext =
    gameChips && gameChips.length > 0 && !gameChips.some((chip) => chip.id === (r.game_id || r.id));

  // Derive online venues from game.source if prop not provided
  let onlineVenues = onlineVenuesProp || (r.source === "online" ? [{ venue_code: "online", access_type: "free" }] : []);

  // If getGameAvailability function is provided, use bulk availability data
  if (getGameAvailability && typeof getGameAvailability === 'function') {
    const gameId = r.game_id || r.id;
    const availabilityData = getGameAvailability(gameId);
    if (availabilityData && availabilityData.venues && availabilityData.venues.length > 0) {
      onlineVenues = availabilityData.venues;
    }
  }

  const onlineInfo = formatOnlineInfo(onlineVenues);

  // Check if user is anonymous
  const isAnonymous = !user || !user.id;

  const { navigate } = useOptionalRouter();

  const handleOpenInExplore = () => {
    if (!navigate || !r) return;
    const gameToExplore = {
      ...r,
      id: r.id || r.game_id,
      game_id: r.game_id || r.id,
    };
    navigate('/', { state: { exploreGame: gameToExplore } });
  };

  // Early return if game object is undefined (after all hooks have been called)
  if (!r) {
    return null;
  }

  const handleCompare = () => {
    // Use new onCompare prop if available, otherwise fallback to old method
    if (typeof onCompare === 'function') {
      onCompare(r);
    } else if (gameChips && gameChips.length > 0 && onAddToSearch) {
      const currentGameName = r.name || `Game ${r.game_id || r.id || ""}`.trim();
      onAddToSearch(currentGameName, "compare", r.game_id || r.id);
    }
  };

  const gameName = r.name || `Game ${r.game_id || r.id || ""}`.trim();
  const detailsParts = [];

  if (r.year_published) detailsParts.push(`${r.year_published}`);
  if (r.average_rating !== null && r.average_rating !== undefined) {
    const ratingLine = `Rating: ${Number(r.average_rating).toFixed(1)}`;
    detailsParts.push(
      r.num_ratings !== null && r.num_ratings !== undefined
        ? `${ratingLine} (${Number(r.num_ratings).toLocaleString()} ratings)`
        : ratingLine
    );
  }
  if (userRating) {
    if (userRating === 'like_a_lot') {
      detailsParts.push('Love');
    } else if (userRating === 'like') {
      detailsParts.push('Like');
    } else if (userRating === 'neutral') {
      detailsParts.push('Neutral');
    } else if (userRating === 'dislike') {
      detailsParts.push('Dislike');
    }
  }
  if (r.min_players || r.max_players) detailsParts.push(`${r.min_players || "?"} - ${r.max_players || "?"} players`);
  if (r.playing_time || r.min_playtime || r.max_playtime) {
    const playtime = r.playing_time
      ? `${r.playing_time} min`
      : r.min_playtime && r.max_playtime
      ? `${r.min_playtime}-${r.max_playtime} min`
      : r.min_playtime
      ? `${r.min_playtime}+ min`
      : r.max_playtime
      ? `Up to ${r.max_playtime} min`
      : "?";
    detailsParts.push(playtime);
  }
  if (r.avg_weight) detailsParts.push(`${r.avg_weight.toFixed(1)}/5 weight`);
  if (r.categories && r.categories.length > 0) detailsParts.push(`Categories: ${r.categories.join(", ")}`);
  if (r.mechanics && r.mechanics.length > 0) detailsParts.push(`Mechanics: ${r.mechanics.join(", ")}`);

  const shareText =
    detailsParts.length > 0
      ? `Look what I found in Pista!\n${gameName}\n${detailsParts.join("\n")}\n\nTry it out at ${FRONTEND_URL}`
      : `Look what I found in Pista!\n${gameName}\n\nTry it out at ${FRONTEND_URL}`;

  const setTransientStatus = (setter, ref, value) => {
    setter(value);
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => setter(""), 1500);
  };

  const tryShareFallback = async () => {
    const shareContent = shareText;

    if (navigator?.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(shareContent);
        setTransientStatus(setShareStatus, shareTimeoutRef, "Game details copied to clipboard!");
        return;
      } catch (err) {
        logger.error("Clipboard API failed:", err);
      }
    }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = shareContent;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        setTransientStatus(setShareStatus, shareTimeoutRef, "Link copied to clipboard!");
      } else {
        setTransientStatus(setShareStatus, shareTimeoutRef, "Share not available");
      }
    } catch (err) {
      logger.error("Fallback copy failed:", err);
      setTransientStatus(setShareStatus, shareTimeoutRef, "Share not available");
    }
  };

  const handleShare = async () => {
    const isNativeMobile = Capacitor.isNativePlatform();
    
    // Show Firefox warning if applicable
    if (isFirefox() && !isNativeMobile) {
      setShowShareWarning(true);
    }

    if (isNativeMobile) {
      try {
        await Share.share({
          title: gameName,
          text: shareText,
        });
        setTransientStatus(setShareStatus, shareTimeoutRef, "Shared");
        return;
      } catch (err) {
        logger.error("Capacitor Share failed:", err);
        if (err.name === "AbortError") {
          setTransientStatus(setShareStatus, shareTimeoutRef, "Share cancelled");
          return;
        }
        tryShareFallback();
        return;
      }
    }

    if (navigator?.share && !isNativeMobile) {
      try {
        await navigator.share({ title: gameName, text: shareText });
        setTransientStatus(setShareStatus, shareTimeoutRef, "Shared");
        return;
      } catch (err) {
        logger.error("Web Share API failed:", err);
        if (err.name === "AbortError") {
          setTransientStatus(setShareStatus, shareTimeoutRef, "Share cancelled");
          return;
        }

        if (err.name === "NotAllowedError" || err.name === "NotSupportedError") {
          tryShareFallback();
          return;
        }

        setTransientStatus(setShareStatus, shareTimeoutRef, "Share cancelled");
        return;
      }
    }

    tryShareFallback();
  };

  const resolvedOwnershipStatus = (() => {
    if (excludeStatus) return excludeStatus;
    if (typeof getGameOwnershipStatus === 'function') {
      return getGameOwnershipStatus(r.game_id || r.id) || false;
    }
    return isInCollection ? 'owned' : false;
  })();

  const ownershipLabel = (() => {
    switch ((resolvedOwnershipStatus || '').toLowerCase()) {
      case 'owned': return 'I have this';
      case 'interested': return 'Interested';
      case 'excluded': return 'Not for me';
      case 'no_shelf': return 'Played';
      default: return resolvedOwnershipStatus ? 'Played' : null;
    }
  })();

  const isCollectionRecommendation =
    querySpec?.intent === "collection_recommendation" &&
    (r.missing_mechanics || r.missing_categories || r.extra_mechanics || r.extra_categories);

  // Normalize similarity score from different API response formats
  const getSimilarityScore = (gameData) => {
    if (gameData.similarity_score !== undefined && gameData.similarity_score !== null) {
      return gameData.similarity_score;
    }
    if (gameData.final_score !== undefined && gameData.final_score !== null) {
      return gameData.final_score;
    }
    if (gameData.embedding_similarity !== undefined && gameData.embedding_similarity !== null) {
      return gameData.embedding_similarity;
    }
    if (gameData.meta_score !== undefined && gameData.meta_score !== null) {
      return gameData.meta_score;
    }
    return null;
  };

  const sharedFeatures = [];
  const hasSharedFeatures = r.shared_mechanics || r.shared_categories || r.shared_designers || r.shared_families;

  // Prioritize shared_* fields when available (comparing two games)
  if (hasSharedFeatures) {
    if (r.shared_mechanics) r.shared_mechanics.forEach((m) => sharedFeatures.push({ type: "mechanics", value: m }));
    if (r.shared_categories) r.shared_categories.forEach((c) => sharedFeatures.push({ type: "categories", value: c }));
    if (r.shared_designers) r.shared_designers.forEach((d) => sharedFeatures.push({ type: "designers", value: d }));
    if (r.shared_families) r.shared_families.forEach((f) => sharedFeatures.push({ type: "families", value: f }));
  } else {
    // Fall back to individual game features when comparing against single game context
    if (r.mechanics) r.mechanics.forEach((m) => sharedFeatures.push({ type: "mechanics", value: m }));
    if (r.categories) r.categories.forEach((c) => sharedFeatures.push({ type: "categories", value: c }));
    if (r.designers_list) r.designers_list.forEach((d) => sharedFeatures.push({ type: "designers", value: d }));
    if (r.families) r.families.forEach((f) => sharedFeatures.push({ type: "families", value: f }));
  }

  const missingFeatures = [];
  const extraFeatures = [];

  if (isCollectionRecommendation) {
    if (r.missing_mechanics) r.missing_mechanics.forEach((m) => missingFeatures.push({ type: "mechanics", value: m }));
    if (r.missing_categories) r.missing_categories.forEach((c) => missingFeatures.push({ type: "categories", value: c }));
    if (r.missing_designers) r.missing_designers.forEach((d) => missingFeatures.push({ type: "designers", value: d }));
    if (r.missing_families) r.missing_families.forEach((f) => missingFeatures.push({ type: "families", value: f }));
    if (r.extra_mechanics) r.extra_mechanics.forEach((m) => extraFeatures.push({ type: "mechanics", value: m }));
    if (r.extra_categories) r.extra_categories.forEach((c) => extraFeatures.push({ type: "categories", value: c }));
    if (r.extra_designers) r.extra_designers.forEach((d) => extraFeatures.push({ type: "designers", value: d }));
    if (r.extra_families) r.extra_families.forEach((f) => extraFeatures.push({ type: "families", value: f }));
  }

  const isUnique =
    variant &&
    differences &&
    ((variant === "A" && differences.onlyInA.some((g) => g.game_id === r.game_id)) ||
      (variant === "B" && differences.onlyInB.some((g) => g.game_id === r.game_id)));

  const openGamePreview = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!setPreviewGame) return;
    try {
      const gameId = r.game_id || r.id;
      const res = await httpRequest(`${API_BASE}/games/${gameId}/details`, {
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        setPreviewGame(await res.json());
      } else {
        setPreviewGame(r);
      }
    } catch (error) {
      logger.error("Failed to fetch game details:", error);
      setPreviewGame(r);
    }
  };

  const displayName = r.name || `Game ${r.game_id || r.id || ""}`.trim();
  const similarityScore = !hideSimilarity ? getSimilarityScore(r) : null;

  return (
    <>
      <div
        className={`curate-game-tile game-card ${isUnique ? "ab-test-unique" : ""}`}
        style={{
          border: isUnique ? "2px solid #1976d2" : "",
          backgroundColor: isUnique ? "rgba(25, 118, 210, 0.05)" : "",
          opacity: chipsToShow === 0 ? 0.3 : 1,
          transition: "opacity 0.3s ease-in",
        }}
      >
        <div
          className={`curate-game-tile-cover${r.thumbnail ? "" : " curate-game-tile-cover--placeholder"}`}
          onClick={openGamePreview}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openGamePreview(e);
            }
          }}
          role="button"
          tabIndex={0}
          title="Click to preview game details"
          aria-label={`Preview ${displayName}`}
        >
          {r.thumbnail ? (
            <img src={r.thumbnail} alt={displayName} className="game-tile-image" />
          ) : (
            <span className="curate-game-tile-cover-fallback" aria-hidden="true">
              {GAME_ICON}
            </span>
          )}
        </div>

        <div className="curate-game-tile-body">
          <div className="curate-game-tile-top-actions">
            <div className="curate-game-tile-top-right-group">
              {showAddToCollection &&
                (ownershipLabel ? (
                  <button
                    type="button"
                    className={`curate-game-tile-status curate-game-tile-status--${resolvedOwnershipStatus}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onAddToCollection) {
                        onAddToCollection();
                      } else {
                        setAddGameModal({
                          game: r,
                          initialData: { ownership_status: resolvedOwnershipStatus },
                        });
                      }
                    }}
                    title={`Click to edit: ${ownershipLabel}`}
                    aria-label={`Edit game status: ${ownershipLabel}`}
                    data-testid="ownership-badge"
                    style={{ cursor: 'pointer' }}
                  >
                    {ownershipLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      logger.debug("[CurateGameTile] Add to Collection clicked", {
                        game: r,
                        id: r.id,
                        game_id: r.game_id,
                        onAddToCollection,
                      });
                      if (onAddToCollection) {
                        onAddToCollection();
                      } else {
                        setAddGameModal({
                          game: r,
                          initialData: { ownership_status: resolvedOwnershipStatus },
                        });
                      }
                    }}
                    title={isAnonymous ? "Login to add games to your library" : "Add to Library"}
                    disabled={isAnonymous}
                    className="curate-game-tile-button curate-game-tile-button--icon"
                  >
                    <Plus size={16} />
                  </button>
                ))}

              {onTogglePlayList && (
                <PlayListButton
                  isActive={inPlayList}
                  isAnonymous={isAnonymous}
                  onClick={() => onTogglePlayList(r)}
                  className="curate-game-tile-button curate-game-tile-button--icon"
                  dataTestId="curate-game-tile-playlist-button"
                />
              )}

              {!user ? (
                <button
                  type="button"
                  disabled
                  title="Login to mark games as Not for me"
                  className="curate-game-tile-button curate-game-tile-button--icon"
                  data-testid="exclude-button-disabled"
                >
                  <Ban size={16} />
                </button>
              ) : !resolvedOwnershipStatus ? (
                <button
                  type="button"
                  onClick={() => setAddGameModal({ game: r, initialData: { ownership_status: "excluded" } })}
                  className="curate-game-tile-button curate-game-tile-button--icon"
                  title="Mark as Not for me"
                  data-testid="exclude-button"
                >
                  <Ban size={16} />
                </button>
              ) : null}

              <button
                type="button"
                onClick={handleShare}
                title={shareStatus || "Share game"}
                aria-label="Share game"
                className="curate-game-tile-button curate-game-tile-button--icon"
              >
                <Share2 size={16} />
              </button>
              
              {showShareWarning && (
                <ShareWarningBanner 
                  message="Share functionality may be limited in Firefox. Contents copied to clipboard, ready to share."
                  className="curate-game-tile-share-warning"
                />
              )}
            </div>
          </div>

          <button
            type="button"
            className="curate-game-tile-title"
            onClick={openGamePreview}
            title="Click to preview game details"
          >
            {displayName}
          </button>

          {(r.year_published || (r.designers && r.designers.length > 0)) && (
            <p className="curate-game-tile-subtitle">
              {r.year_published}
              {r.year_published && r.designers && r.designers.length > 0 && ' · '}
              {formatDesigners(r.designers, 2)}
            </p>
          )}

          {r.recommendation_tag && (
            <span
              className={`curate-game-tile-rec-tag curate-game-tile-rec-tag--${r.recommendation_tag.toLowerCase()}`}
              title={
                r.recommendation_tag === "Complement"
                  ? "Adds different mechanics at similar complexity"
                  : "Similar mechanics with a higher rating — potential upgrade"
              }
            >
              {r.recommendation_tag === "Complement" ? (<> <Sparkles size={14} /> Complement</>) : (<> <ArrowUp size={14} /> Replace</>)}
            </span>
          )}

          <div className="curate-game-tile-meta">
            {(r.min_players || r.max_players) && (
              <span className="curate-game-tile-meta-item">
                {GROUP_ICON} {r.min_players || "?"}-{r.max_players || "?"}
              </span>
            )}
            {(r.playing_time || r.min_playtime || r.max_playtime) && (
              <span className="curate-game-tile-meta-item">
                {TIMER_ICON}
                {r.playing_time
                  ? `${r.playing_time}m`
                  : r.min_playtime && r.max_playtime
                  ? `${r.min_playtime}-${r.max_playtime}m`
                  : r.min_playtime
                  ? `${r.min_playtime}+m`
                  : r.max_playtime
                  ? `≤${r.max_playtime}m`
                  : "?"}
              </span>
            )}
            {r.average_rating !== null && r.average_rating !== undefined && (
              <span className="curate-game-tile-meta-item">
                {RATING_ICON} {Number(r.average_rating).toFixed(1)}
                {r.num_ratings !== null && r.num_ratings !== undefined
                  ? ` (${Number(r.num_ratings).toLocaleString()} ratings)`
                  : ""}
              </span>
            )}
            {similarityScore !== null && (
              <span
                className={`curate-game-tile-meta-item${
                  isCollectionRecommendation ? " curate-game-tile-meta-item--emphasis" : ""
                }`}
              >
                Similarity: {(similarityScore * 100).toFixed(1)}%
              </span>
            )}
            {r.personal_rating && (
              <span className="curate-game-tile-meta-item curate-game-tile-meta-item--personal">
                {GAME_ICON} Personal:{" "}
                {typeof r.personal_rating === "number" ? r.personal_rating.toFixed(1) : r.personal_rating}
              </span>
            )}
          </div>

          {(onlineInfo || onAddToMarketplace) && (
            <div className="curate-game-tile-marketplace-row">
              {onlineInfo && (
                <span className="curate-game-tile-highlight" title={onlineInfo.title}>
                  {onlineInfo.text}
                </span>
              )}
              {marketplaceSummary?.label && (
                <span className="curate-game-tile-highlight" title="Used copy marketplace summary">
                  {marketplaceSummary.label}
                </span>
              )}
              {onAddToMarketplace && (
                <button
                  type="button"
                  onClick={() => onAddToMarketplace(r)}
                  className="curate-game-tile-view-button"
                  title="View listings"
                  data-testid="view-button"
                >
                  View listings
                </button>
              )}
            </div>
          )}

          {!hideSimilarity &&
            (sharedFeatures.length > 0 || missingFeatures.length > 0 || extraFeatures.length > 0) && (
              <div className="curate-game-tile-comparison game-comparison">
                {sharedFeatures.length > 0 && (
                  <div className="comparison-section shared">
                    <span className="curate-game-tile-comparison-label curate-game-tile-comparison-label--shared">
                      Similarities:{" "}
                    </span>
                    <span className="curate-game-tile-comparison-values">
                      {sharedFeatures
                        .slice(0, 3)
                        .map((f) => f.value)
                        .join(", ")}
                      {sharedFeatures.length > 3 && ` +${sharedFeatures.length - 3} more`}
                    </span>
                  </div>
                )}
                {missingFeatures.length > 0 && (
                  <div className="comparison-section missing">
                    <span className="curate-game-tile-comparison-label curate-game-tile-comparison-label--missing">
                      {WARNING_ICON} Missing (in target game):{" "}
                    </span>
                    <span className="curate-game-tile-comparison-values">
                      {missingFeatures
                        .slice(0, 3)
                        .map((f) => f.value)
                        .join(", ")}
                      {missingFeatures.length > 3 && ` +${missingFeatures.length - 3} more`}
                    </span>
                  </div>
                )}
                {extraFeatures.length > 0 && (
                  <div className="comparison-section extra">
                    <span className="curate-game-tile-comparison-label curate-game-tile-comparison-label--extra">
                      ➕ Extra (in your collection game):{" "}
                    </span>
                    <span className="curate-game-tile-comparison-values">
                      {extraFeatures
                        .slice(0, 3)
                        .map((f) => f.value)
                        .join(", ")}
                      {extraFeatures.length > 3 && ` +${extraFeatures.length - 3} more`}
                    </span>
                  </div>
                )}
              </div>
            )}

          <div className="curate-game-tile-actions">
            <div className="curate-game-tile-actions-buttons">
              {hasGamesInContext && (
                <button
                  type="button"
                  className="curate-game-tile-button"
                  onClick={handleCompare}
                  title={`Compare ${displayName}`}
                  aria-label="Compare game"
                >
                  Compare
                </button>
              )}

              {onSearchSimilar && (
                <button
                  type="button"
                  data-testid="find-similar-button"
                  onClick={() => onSearchSimilar(r)}
                  className="curate-game-tile-button secondary"
                  title="Find similar games"
                >
                  Find Similar{' '}
                  <span style={{ fontSize: '0.65em', verticalAlign: 'super', opacity: 0.7 }}>beta</span>
                </button>
              )}

              {!hideOpenInExplore && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInExplore();
                  }}
                  className="curate-game-tile-button secondary"
                  title="Open this game in Explore"
                  data-testid="open-in-explore-button"
                >
                  Open in Explore
                </button>
              )}


              {user && !resolvedOwnershipStatus ? (
                <button
                  type="button"
                  onClick={() => setAddGameModal({ game: r, initialData: { ownership_status: "interested" } })}
                  className="curate-game-tile-button secondary"
                  title="Add to Interested list"
                  data-testid="interested-button"
                >
                  Interested
                </button>
              ) : (
                !user && (
                  <button
                    type="button"
                    disabled
                    title="Login to add games to your collection"
                    className="curate-game-tile-button"
                    data-testid="interested-button-disabled"
                  >
                    Interested 🔒
                  </button>
                )
              )}

              {setPreviewGame && (
                <button
                  type="button"
                  onClick={openGamePreview}
                  className="curate-game-tile-button curate-game-tile-button--launch"
                  title="Go to Session"
                  aria-label="Go to Session"
                >
                  Go to Session
                </button>
              )}

              {onLetSPlay && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isAnonymous) {
                      onLetSPlay(r);
                    }
                  }}
                  className="curate-game-tile-button curate-game-tile-button--launch"
                  title={isAnonymous ? "Login to log game sessions" : "Launch game session"}
                  disabled={isAnonymous}
                >
                  Score & Log{isAnonymous ? " 🔒" : ""}
                </button>
              )}
            </div>

          </div>

          {!hideFeedback && (
            <div className="curate-game-tile-feedback" data-testid="rec-feedback">
              <span className="curate-game-tile-feedback-label">Helpful?</span>
              <button
                type="button"
                className={`curate-game-tile-feedback-btn${feedbackSignal === 'thumbs_up' ? ' curate-game-tile-feedback-btn--active-up' : ''}${!user ? ' curate-game-tile-feedback-btn--anon' : ''}`}
                onClick={() => handleFeedback('thumbs_up')}
                title={user ? "Good recommendation" : "Sign in to rate recommendations"}
                aria-label="Thumbs up"
                data-testid="feedback-up"
                disabled={!user}
              >
                👍
              </button>
              <button
                type="button"
                className={`curate-game-tile-feedback-btn${feedbackSignal === 'thumbs_down' ? ' curate-game-tile-feedback-btn--active-down' : ''}${!user ? ' curate-game-tile-feedback-btn--anon' : ''}`}
                onClick={() => handleFeedback('thumbs_down')}
                title={user ? "Not a good recommendation" : "Sign in to rate recommendations"}
                aria-label="Thumbs down"
                data-testid="feedback-down"
                disabled={!user}
              >
                👎
              </button>
            </div>
          )}
        </div>
      </div>

    {addGameModal && (
      <AddGameModal
        isOpen={true}
        game={addGameModal.game}
        initialData={addGameModal.initialData}
        user={user}
        onClose={() => setAddGameModal(null)}
        onSave={(savedGame) => {
          setAddGameModal(null);
          if (savedGame?.ownership_status) {
            setExcludeStatus(savedGame.ownership_status);
          }
          if (savedGame?.ownership_status === "excluded") {
            onExcludeGame?.(r);
          }
        }}
      />
    )}
    </>
  );
}

export { GameTile, formatDesigners };
